import { CheckIcon, ChevronDownIcon, I8NIcon } from '@/components/icons';
import { useManifest } from '@/contexts/ManifestContext';
import { cn } from '@/utils/cn';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearch } from 'wouter-preact';

interface LanguageSelectorProps {
  /**
   * `dropdown` (default) renders a globe button with an absolute popover — used
   * in the desktop header. `inline` renders a full-width row that expands the
   * language list in flow — used inside the mobile slide menu.
   */
  variant?: 'dropdown' | 'inline';
  /** Called after a language is selected (e.g. to close the slide menu). */
  onSelect?: () => void;
}

/** Native language name, e.g. `en` → "English", `nl` → "Nederlands". */
const languageLabel = (lang: string): string => {
  try {
    const name = new Intl.DisplayNames([lang], { type: 'language' }).of(lang);
    return name
      ? name.charAt(0).toUpperCase() + name.slice(1)
      : lang.toUpperCase();
  } catch {
    return lang.toUpperCase();
  }
};

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = 'dropdown',
  onSelect,
}: LanguageSelectorProps) => {
  const { locale } = useManifest();
  const { t, i18n } = useTranslation();
  const searchString = useSearch();
  const [location, setLocation] = useLocation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleLanguageClick = (newLanguage: string) => {
    const segments = location.split('/').filter((segment) => segment !== '');
    const isValidLocale = segments[0] && locale.supported.includes(segments[0]);

    let newPath: string;
    if (isValidLocale) {
      // Replace the first segment with the new language
      segments[0] = newLanguage;
      newPath = '/' + segments.join('/');
    } else {
      // Add the new language as the first segment
      newPath = '/' + newLanguage + location;
    }

    const queryParams = searchString ? `?${searchString}` : '';
    setLocation(newPath + queryParams);
    setOpen(false);
    onSelect?.();
  };

  // Keep i18n in sync with the locale segment in the URL.
  useEffect(() => {
    const segments = location.split('/').filter((segment) => segment !== '');

    if (!segments[0]) return;
    if (segments[0] === i18n.language) return;

    if (locale.supported.includes(segments[0])) {
      i18n.changeLanguage(segments[0]);
    }
  }, [location]);

  // Close the popover when clicking outside (dropdown variant only).
  useEffect(() => {
    if (variant !== 'dropdown' || !open) return;

    const handleOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [variant, open]);

  const isInline = variant === 'inline';

  const languageList = (
    <ul
      className={cn('flex w-full flex-col', isInline ? 'mt-6 gap-6' : 'gap-1')}
    >
      {locale.supported.map((lang) => {
        const active = i18n.language === lang;
        return (
          <li key={lang}>
            <button
              onClick={() => handleLanguageClick(lang)}
              className={cn(
                'text-primary-alt font-body flex w-full cursor-pointer items-center justify-between gap-3 text-left font-medium transition-colors',
                isInline
                  ? 'pl-8 text-xl hover:opacity-70'
                  : 'hover:bg-neutral-lightest h-8 rounded-full px-3 text-sm',
              )}
            >
              <span>{languageLabel(lang)}</span>
              {active && (
                <CheckIcon className="text-primary h-4 w-4 shrink-0" />
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );

  if (isInline) {
    return (
      <div ref={containerRef} className="text-primary-alt">
        <button
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex w-full cursor-pointer items-center gap-2"
        >
          <I8NIcon className="h-6 w-6 shrink-0" />
          <span className="font-body flex-1 text-left text-xl font-medium">
            {t('layout.menu.selectLanguage')}
          </span>
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 transition-transform',
              open ? 'rotate-180' : '',
            )}
          />
        </button>
        {open && languageList}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={t('layout.menu.selectLanguage')}
        className="text-primary-alt hover:bg-neutral-lightest flex h-8 w-8 cursor-pointer items-center justify-center rounded-full transition-colors"
      >
        <I8NIcon className="h-5 w-5" />
      </button>
      {open && (
        <div className="border-neutral-light bg-neutral-white absolute right-0 z-110 mt-3 min-w-50 rounded-lg border p-4 shadow-lg">
          <p className="text-primary-alt font-body px-3 pb-2 text-sm font-bold">
            {t('layout.menu.selectLanguage')}
          </p>
          {languageList}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
